import { useId } from 'react';

export interface DualRangeSliderProps {
  label: string;
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue?: (value: number) => string;
  disabled?: boolean;
  id?: string;
}

export function DualRangeSlider({
  label,
  min,
  max,
  value,
  onChange,
  formatValue = String,
  disabled = false,
  id,
}: DualRangeSliderProps) {
  const generatedId = useId();
  const rootId = id ?? generatedId;
  const range = max - min;

  const low = Math.min(value[0], value[1]);
  const high = Math.max(value[0], value[1]);

  const lowPct = range === 0 ? 0 : ((low - min) / range) * 100;
  const highPct = range === 0 ? 100 : ((high - min) / range) * 100;

  function handleLowChange(nextLow: number) {
    if (nextLow > high) {
      onChange([high, nextLow]);
    } else {
      onChange([nextLow, high]);
    }
  }

  function handleHighChange(nextHigh: number) {
    if (nextHigh < low) {
      onChange([nextHigh, low]);
    } else {
      onChange([low, nextHigh]);
    }
  }

  return (
    <div className={`w-full ${disabled ? 'opacity-50' : ''}`}>
      <label htmlFor={rootId} className="block text-xs font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative h-6">
        <div className="absolute top-2.5 left-0 right-0 h-1.5 bg-gray-200 rounded" />
        <div
          className="absolute top-2.5 h-1.5 bg-blue-500 rounded"
          style={{ left: `${lowPct}%`, right: `${100 - highPct}%` }}
        />
        <input
          id={`${rootId}-low`}
          type="range"
          min={min}
          max={max}
          value={low}
          disabled={disabled || range === 0}
          onChange={(e) => handleLowChange(Number(e.target.value))}
          aria-label={`${label} minimum`}
          className="absolute top-0 left-0 w-full h-6 pointer-events-none appearance-none bg-transparent slider-thumb"
        />
        <input
          id={`${rootId}-high`}
          type="range"
          min={min}
          max={max}
          value={high}
          disabled={disabled || range === 0}
          onChange={(e) => handleHighChange(Number(e.target.value))}
          aria-label={`${label} maximum`}
          className="absolute top-0 left-0 w-full h-6 pointer-events-none appearance-none bg-transparent slider-thumb"
        />
      </div>
      <div className="flex justify-between text-[10px] text-gray-500 mt-0.5">
        <span aria-hidden="true">{formatValue(low)}</span>
        <span aria-hidden="true">{formatValue(high)}</span>
      </div>
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          pointer-events: auto;
          -webkit-appearance: none;
          appearance: none;
          width: 1rem;
          height: 1rem;
          border-radius: 9999px;
          background: #2563eb;
          border: 2px solid white;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          margin-top: -0.25rem;
        }
        .slider-thumb::-moz-range-thumb {
          pointer-events: auto;
          width: 1rem;
          height: 1rem;
          border-radius: 9999px;
          background: #2563eb;
          border: 2px solid white;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
